'use client';

import { useEffect, type ReactNode } from 'react';
import Lenis from 'lenis';

/**
 * Wraps the page in a Lenis smooth-scroll instance.
 *
 * Lenis hijacks the scroll wheel to produce a buttery, interpolated scroll
 * — the thing that makes Apple / Linear / Rauno feel luxury. It also emits
 * a global `scroll` event so GSAP ScrollTrigger stays perfectly in sync
 * (see ScrollTrigger.scrollerProxy isn't needed when we call
 * ScrollTrigger.update() from the lenis raf).
 *
 * Respects `prefers-reduced-motion`: the smoothing coefficient collapses
 * to 1 (effectively native scroll) when the user opts out.
 */
export function SmoothScroll({ children }: { children: ReactNode }) {
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const lenis = new Lenis({
      duration: prefersReduced ? 0 : 1.15,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo-out
      smoothWheel: !prefersReduced,
      touchMultiplier: 1.6,
    });

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    // Sync with GSAP ScrollTrigger if it's active on the page.
    import('gsap').then(({ gsap }) => {
      import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
        gsap.registerPlugin(ScrollTrigger);
        lenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.lagSmoothing(0);
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return <>{children}</>;
}
